import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { Row, Col, Button, Form } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import fr from "date-fns/locale/fr";
import { useTranslation } from 'react-i18next';

import config from '../config/config';
import { useWebsite } from '../contexts/WebsiteContext';
import { useResto } from '../contexts/RestoContext';

const StatDayVisitors = () => {
    const { t } = useTranslation();
    const { restoData } = useResto();
    const [visitorData, setVisitorData] = useState([]);
    const [sources, setSources] = useState([]);
    const [selectedSource, setSelectedSource] = useState('all');
    const [supports, setSupports] = useState([]);
    const [selectedSupport, setSelectedSupport] = useState('all');
    const { website, setIsLoading } = useWebsite();

    const getDefaultDates = () => {
        const today = new Date();
        const defaultEndDate = new Date(today);
        defaultEndDate.setDate(today.getDate() - 1);

        const defaultStartDate = new Date(defaultEndDate);
        defaultStartDate.setDate(defaultEndDate.getDate() - 30);

        return { defaultStartDate, defaultEndDate };
    };

    const { defaultStartDate, defaultEndDate } = getDefaultDates();
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);
    const [filteredData, setFilteredData] = useState([]);

    const { kpiUrl, productName } = config;
    const apiPath = `${kpiUrl}count-visits-datas`;

    const fetchData = async () => {
        if (!website) {
            return;
        }
        const formatDateForAPI = (date) => {
            const year = date.getFullYear();
            const month = (`0${date.getMonth() + 1}`).slice(-2);
            const day = (`0${date.getDate()}`).slice(-2);
            return `${year}${month}${day}`;
        };

        const start = formatDateForAPI(startDate);
        const end = formatDateForAPI(endDate);

        try {
            setIsLoading(true);
            const response = await axios.get(`${apiPath}?dataType=dayVisitor&productName=${productName}&website=${website}&start=${start}&end=${end}`);
            const sources = [...new Set(response.data.map(item => item.source))]; // Extract unique sources
            const supports = [...new Set(response.data.map(item => item.support))]; // Extract unique supports
            setSources(sources);
            setSupports(supports);
            setVisitorData(response.data);
            const filtered = filterData(response.data, selectedSource, selectedSupport);
            const aggregatedData = aggregateData(filtered);
            setFilteredData(aggregatedData);
        } catch (error) {
            console.error('Error fetching data', error);
        }
        setIsLoading(false);
    };

    const filterData = (data, source, support) => {
        let filtered = data;
        if (source !== 'all') {
            filtered = filtered.filter(item => item.source === source);
        }
        if (support !== 'all') {
            filtered = filtered.filter(item => item.support === support);
        }
        return filtered;
    };

    const aggregateData = (data) => {
        const aggregated = {};
        data.forEach(item => {
            if (!aggregated[item.yearmonthday]) {
                aggregated[item.yearmonthday] = 0;
            }
            aggregated[item.yearmonthday] += item.count;
        });

        return Object.keys(aggregated).map(date => ({
            date: formatDate(new Date(date.substring(0, 4), date.substring(4, 6) - 1, date.substring(6, 8))),
            count: aggregated[date]
        }));
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const filtered = filterData(visitorData, selectedSource, selectedSupport);
        const aggregatedData = aggregateData(filtered);
        setFilteredData(aggregatedData);
    }, [selectedSource, selectedSupport]);

    const formatDate = (date) => {
        const day = (`0${date.getDate()}`).slice(-2);
        const month = (`0${date.getMonth() + 1}`).slice(-2);
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const chartData = {
        labels: filteredData.map(item => item.date),
        datasets: [
            {
                data: filteredData.map(item => item.count),
                fill: false,
                backgroundColor: `rgb(${restoData.mainColor})`,
                borderColor: `rgb(${restoData.mainColor})`,
                tension: 0.4,
            },
        ],
    };

    const chartOptions = {
        scales: {
            x: {
                grid: {
                    display: false,
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    display: true,
                    drawBorder: true,
                },
            },
        },
        plugins: {
            legend: {
                display: false
            }
        }
    };

    return (
        <div>
            <div className="kubuni-frame">
                <h2 style={{ marginTop: '0', marginBottom: '20px' }}>Visiteurs par jour</h2>
                <Row>
                    <Col>
                        <div className="d-flex" style={{ margin: '5px 5px 20px 5px' }}>
                            <label style={{ margin: '5px' }}>Source: </label>
                            <Form.Control as="select" value={selectedSource} onChange={(e) => setSelectedSource(e.target.value)} style={{ marginLeft: '5px', paddingLeft: '5px' }}>
                                <option value="all">Toutes les sources</option>
                                {sources.map(source => (
                                    <option key={source} value={source}>{t(source)}</option>
                                ))}
                            </Form.Control>
                        </div>
                    </Col>
                    <Col>
                        <div className="d-flex" style={{ margin: '5px 5px 20px 5px' }}>
                            <label style={{ margin: '5px' }}>Support: </label>
                            <Form.Control as="select" value={selectedSupport} onChange={(e) => setSelectedSupport(e.target.value)} style={{ marginLeft: '5px', paddingLeft: '5px' }}>
                                <option value="all">Tous les supports</option>
                                {supports.map(support => (
                                    <option key={support} value={support}>{t(support)}</option>
                                ))}
                            </Form.Control>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col md={4} className='text-center'>
                        <span style={{ margin: '5px' }}>Du </span>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            dateFormat="dd/MM/yyyy"
                            locale={fr}
                            className='text-center form-control'
                        />
                    </Col>
                    <Col md={4} className='text-center'>
                        <span style={{ margin: '5px' }}> au </span>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            dateFormat="dd/MM/yyyy"
                            locale={fr}
                            className='text-center form-control'
                            style={{ padding: '0px' }}
                        />
                    </Col>
                    <Col md={4} className='text-center'>
                        <Button variant="primary" className='' onClick={fetchData} style={{ margin: '5px auto' }}>Filtrer cette période</Button>
                    </Col>
                </Row>
                <div className="mt-4">
                    <div className="chart-block">
                        <Line data={chartData} options={chartOptions} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StatDayVisitors;
